import React, { useState, useEffect } from 'react';
import { usePermissions, useTranslate } from 'react-admin';
import { merge } from 'lodash';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '~/components/TableCell';
import Grid from '@material-ui/core/Grid';
import SvgIcon from '@material-ui/core/SvgIcon';

import { makeStyles } from '@material-ui/core/styles';

import { useApi, useApiLazy, useNotify } from '~/hooks';
import { getIdentityRates, setIdentityRates } from '~/api/partners';
import { IntegrationApiIdentityType } from '~/api/partners';
import { FlashIcon } from '~/img';

import { l } from '../PartnerCreate';
import NumberInput from '~/components/NumberInput';
import { Button } from 'react-admin';

export interface Props {
  identity: IntegrationApiIdentityType;
}

const RateLimiter = ({ identity }: Props) => {
  const [{ data: initialState }] = useApi<any>(() => getIdentityRates(identity.partnerId));

  const [state, setState] = useState<any>({});
  useEffect(() => {
    setState(initialState || {});
  }, [initialState]);

  const [updateRatesQuery, { loading: updatedRatesLoading }] = useApiLazy<any>(
    setIdentityRates,
    [],
    {
      onSuccess: () => {
        notify({ message: t('ratesUpdateSuccess'), type: 'success' });
      },
      onError: () => {
        notify({ message: t('ratesUpdateError'), type: 'error' });
      },
    }
  );

  const translate = useTranslate();
  const t = (key: string): string => translate(l(`rateLimiter.${key}`));

  const classes = useStyles();
  const notify = useNotify();

  const { permissions } = usePermissions();
  if (!permissions) return null;

  const scopes = ['transaction', 'other'];

  const renderScopeRow = (scope: string) => {
    const currentRates = Object.values(state[scope]?.ratePerSecond || {})
      .map((value: any) => Math.round(value * 100) / 100)
      .join(',');
    const ratePerSecond = state[scope]?.configuration?.ratePerSecond || 0;
    const windowSeconds = state[scope]?.configuration?.windowSeconds || 0;

    const onRatePerSecondChange = (value: number | null) => {
      var newValue = value;
      if (!newValue || newValue < 0) {
        newValue = 0;
      }
      const newState = merge(
        { ...state },
        {
          [scope]: {
            configuration: {
              ratePerSecond: newValue,
            },
          },
        }
      );
      setState(newState);
    };
    const onWindowSecondsChange = (value: number | null) => {
      var newValue = Math.round(value || 0);
      if (newValue > 60) {
        newValue = 60;
      }
      if (newValue < 10) {
        newValue = 10;
      }
      const newState = merge(
        { ...state },
        {
          [scope]: {
            configuration: {
              windowSeconds: newValue,
            },
          },
        }
      );
      setState(newState);
    };

    const shareRatePerSecond = (value: number) => {
      if (!value || value < 0) return 0;
      return value;
    };
    const shareWindowSeconds = (value: number) => {
      if (!value) {
        return 0;
      }
      if (value > 60) {
        return 60;
      }
      if (value < 10) {
        return 10;
      }
      return value;
    };

    return (
      <TableRow key={scope}>
        <TableCell>{t(`scope-${scope}`)}</TableCell>
        <TableCell>{currentRates || '0'}</TableCell>
        <TableCell>
          <NumberInput
            required
            value={ratePerSecond}
            onChange={onRatePerSecondChange}
            shape={shareRatePerSecond}
          />
        </TableCell>
        <TableCell>
          <NumberInput
            required
            value={windowSeconds}
            onChange={onWindowSecondsChange}
            shape={shareWindowSeconds}
          />
        </TableCell>
      </TableRow>
    );
  };

  const onSave = () => {
    updateRatesQuery({
      partnerId: identity.partnerId,
      configuration: state,
    });
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>{t('scope')}</TableCell>
              <TableCell>{t('currentRates')}</TableCell>
              <TableCell>{t('ratePerSecond')}</TableCell>
              <TableCell>{t('windowSeconds')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{scopes.map((scope) => renderScopeRow(scope))}</TableBody>
        </Table>
      </Grid>
      <Grid item xs={12}>
        <Button
          label={t('save')}
          onClick={onSave}
          variant='contained'
          disabled={updatedRatesLoading}
        >
          <SvgIcon>
            <FlashIcon />
          </SvgIcon>
        </Button>
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles(() => ({
  table: {
    minWidth: 600,
  },
}));

export default RateLimiter;
